import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, Download, FileCheck2, LoaderCircle, MapPin, PenLine, RotateCcw, ShieldCheck, Wallet,
} from 'lucide-react';
import logo from '../assets/1.png';
import CountryFlag from '../components/CountryFlag';
import { ContractDocument } from '../components/contract/ContractDocument';
import { contractSourceFor, parseSections } from '../components/contract/contractText';
import { contractFileName, renderContractPdf } from '../components/contract/contractPdf';
import publicContractService, { type PublicQuotePreview } from '../services/publicContract.service';
import studentPortalService from '../services/studentPortal.service';
import type { ContractDetails, PricingCatalogue, PricingQuestion, SignupCountry, StudentContract } from '../types/studentPortal';
import { portalApiError } from '../utils/portalApiError';
import '../layout/StudentPortalLayout.css';
import './PublicContract.css';

/*
 * The public contract page (/contract): no account needed. A visitor chooses a
 * destination, answers its pricing questions, enters their details and downloads
 * the stamped contract. The contract is rendered by the same ContractDocument the
 * student portal uses, from data the server builds with the portal's quote engine.
 */

type Step = 0 | 1 | 2 | 3;
type Destination = { country: SignupCountry; catalogue: PricingCatalogue };

interface PersonForm {
  full_name: string;
  email: string;
  phone: string;
  nationality: string;
  national_id: string;
  passport_number: string;
  address: string;
  qualification: string;
  study_level: string;
  guardian_name: string;
  guardian_national_id: string;
  guardian_phone: string;
  file_notes: string;
}

const EMPTY_PERSON: PersonForm = {
  full_name: '', email: '', phone: '', nationality: '', national_id: '', passport_number: '', address: '',
  qualification: '', study_level: '', guardian_name: '', guardian_national_id: '', guardian_phone: '', file_notes: '',
};

const STEPS = [
  { title: 'الوجهة', icon: MapPin },
  { title: 'الباقة والسعر', icon: Wallet },
  { title: 'بياناتك', icon: PenLine },
  { title: 'العقد', icon: FileCheck2 },
];

const QUALIFICATIONS = ['شهادة الثانوية العامة', 'دبلوم', 'بكالوريوس', 'ماجستير', 'أخرى'];
const STUDY_LEVELS = ['بكالوريوس', 'ماجستير', 'دكتوراه'];
const NATIONALITIES = ['مصري', 'فلسطيني', 'سوري', 'سوداني', 'أردني', 'عراقي', 'سعودي', 'ليبي', 'تونسي', 'جزائري', 'مغربي', 'لبناني', 'روماني'];

// Keeps a half-filled form across a refresh. Per-visitor convenience only:
// failures are ignored and the page works the same without it.
const DRAFT_KEY = 'edunasser.publicContract.draft.v1';
const RESULT_KEY = 'edunasser.publicContract.result.v1';
const readStore = <T,>(store: Storage, key: string): T | null => {
  try { const raw = store.getItem(key); return raw ? (JSON.parse(raw) as T) : null; } catch { return null; }
};
const writeStore = (store: Storage, key: string, value: unknown) => {
  try { if (value === null) store.removeItem(key); else store.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const isVisible = (question: PricingQuestion, answers: Record<string, string>) =>
  !question.visible_when || Object.entries(question.visible_when).every(([key, value]) => answers[key] === value);

const money = (value: string | number, currency: string) => `${Number(value).toLocaleString('ar-EG')} ${currency}`;

const countryNameAr = (code: string, fallback: string) => {
  const upper = code.toUpperCase();
  if (['IT', 'ITA'].includes(upper)) return 'إيطاليا';
  if (['RO', 'ROU'].includes(upper)) return 'رومانيا';
  if (['DE', 'DEU'].includes(upper)) return 'ألمانيا';
  return fallback;
};

/** Arabic messages for the codes this page's API returns; the rest go through portalApiError. */
function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const code = (error.response?.data?.detail as { code?: string } | undefined)?.code;
    if (status === 429 || code === 'TOO_MANY_CONTRACTS') return 'تم إنشاء عدد كبير من العقود من هذا الاتصال. حاول مرة أخرى بعد ساعة.';
    if (code === 'COUNTRY_NOT_AVAILABLE') return 'هذه الوجهة غير متاحة حاليًا. اختر وجهة أخرى.';
    if (code === 'PACKAGE_NOT_AVAILABLE') return 'لا توجد باقة مفعلة لهذه الوجهة حاليًا. تواصل معنا.';
    if (code === 'INVALID_PRICING_ANSWERS') return 'راجع إجابات أسئلة الباقة ثم حاول مرة أخرى.';
    if (code === 'INVALID_PRICING_CONFIGURATION') return 'تعذر حساب السعر لهذه الاختيارات. تواصل معنا وسنساعدك.';
  }
  return portalApiError(error, 'ar', { ar: fallback, en: fallback });
}

function validatePerson(form: PersonForm): string | null {
  const name = form.full_name.trim().replace(/\s+/g, ' ');
  if (name.length < 3 || !name.includes(' ')) return 'الاسم بالكامل: اكتب الاسم الأول واسم العائلة.';
  if (!EMAIL_PATTERN.test(form.email.trim())) return 'البريد الإلكتروني: اكتب بريدًا صحيحًا.';
  const rules: Array<[keyof PersonForm, string, number]> = [
    ['phone', 'رقم الهاتف', 6],
    ['nationality', 'الجنسية', 2],
    ['national_id', 'الرقم القومي / رقم الهوية', 5],
    ['address', 'العنوان', 5],
    ['qualification', 'المؤهل الدراسي', 2],
    ['study_level', 'المرحلة المطلوبة', 2],
  ];
  for (const [key, label, min] of rules) {
    if (form[key].trim().length < min) return `${label}: أدخل ${min} أحرف على الأقل.`;
  }
  return null;
}

export default function PublicContract() {
  const draft = useMemo(() => readStore<{ countryId?: number; answers?: Record<string, string>; person?: Partial<PersonForm> }>(localStorage, DRAFT_KEY), []);
  const savedResult = useMemo(() => readStore<{ token: string; contract: StudentContract; downloaded?: boolean }>(sessionStorage, RESULT_KEY), []);

  const [step, setStep] = useState<Step>(savedResult ? 3 : 0);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loadingDestinations, setLoadingDestinations] = useState(true);
  const [countryId, setCountryId] = useState<number | null>(draft?.countryId ?? null);
  const [answers, setAnswers] = useState<Record<string, string>>(draft?.answers ?? {});
  const [person, setPerson] = useState<PersonForm>({ ...EMPTY_PERSON, ...(draft?.person ?? {}) });
  const [quote, setQuote] = useState<PublicQuotePreview | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [contract, setContract] = useState<StudentContract | null>(savedResult?.contract ?? null);
  const [token, setToken] = useState<string | null>(savedResult?.token ?? null);
  const [downloaded, setDownloaded] = useState(Boolean(savedResult?.downloaded));
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const destination = destinations.find((item) => item.country.id === countryId) ?? null;
  const questions = useMemo(() => destination?.catalogue.questions.filter((q) => isVisible(q, answers)) ?? [], [destination, answers]);
  const answersComplete = Boolean(destination) && questions.every((q) => !q.required || answers[q.key]);
  const sections = useMemo(() => parseSections(contractSourceFor(contract?.template_key, contract?.country.code)), [contract?.template_key, contract?.country.code]);

  // Only destinations with a working pricing catalogue can produce a contract.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const countries = await studentPortalService.getSignupCountries();
        const loaded = await Promise.allSettled(countries.map(async (country) => ({ country, catalogue: await studentPortalService.getPricingCatalogue(country.id) })));
        if (!active) return;
        const priced = loaded.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
        setDestinations(priced);
        if (countryId && !priced.some((item) => item.country.id === countryId)) setCountryId(null);
      } catch (loadError) {
        if (active) setError(errorMessage(loadError, 'تعذر تحميل الوجهات المتاحة. تحقق من الاتصال ثم حدّث الصفحة.'));
      } finally {
        if (active) setLoadingDestinations(false);
      }
    })();
    return () => { active = false; };
    // Load once; countryId is only read to drop a stale draft choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Identity numbers are never kept in the browser, only the easy-to-retype rest.
  useEffect(() => {
    if (contract) return;
    const safe: Partial<PersonForm> = { ...person };
    delete safe.national_id;
    delete safe.passport_number;
    delete safe.guardian_national_id;
    writeStore(localStorage, DRAFT_KEY, { countryId, answers, person: safe });
  }, [contract, countryId, answers, person]);

  // The exact price, from the server, once every required question is answered.
  useEffect(() => {
    if (!destination || !answersComplete) { setQuote(null); return; }
    let active = true;
    setQuoting(true);
    const visibleAnswers = Object.fromEntries(questions.filter((q) => answers[q.key]).map((q) => [q.key, answers[q.key]]));
    const timer = window.setTimeout(() => {
      publicContractService.previewQuote(destination.country.id, visibleAnswers)
        .then((result) => { if (active) { setQuote(result); setError(null); } })
        .catch((quoteError) => { if (active) { setQuote(null); setError(errorMessage(quoteError, 'تعذر حساب السعر. حاول مرة أخرى.')); } })
        .finally(() => { if (active) setQuoting(false); });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [destination, answersComplete, answers, questions]);

  const goTo = (next: Step) => {
    setError(null);
    setStep(next);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const chooseCountry = (id: number) => {
    if (id !== countryId) { setAnswers({}); setQuote(null); }
    setCountryId(id);
    goTo(1);
  };

  const setAnswer = (key: string, value: string) => setAnswers((current) => ({ ...current, [key]: value }));
  const setField = (key: keyof PersonForm, value: string) => { setError(null); setPerson((current) => ({ ...current, [key]: value })); };

  const details: ContractDetails = {
    nationality: person.nationality.trim(),
    national_id: person.national_id.trim(),
    passport_number: person.passport_number.trim() || null,
    address: person.address.trim(),
    phone: person.phone.trim(),
    guardian_name: person.guardian_name.trim() || null,
    guardian_national_id: person.guardian_national_id.trim() || null,
    guardian_phone: person.guardian_phone.trim() || null,
    qualification: person.qualification,
    desired_specialization: null,
    study_level: person.study_level,
    file_notes: person.file_notes.trim() || null,
  };

  const submit = async () => {
    const invalid = validatePerson(person);
    if (invalid) { setError(invalid); return; }
    if (!destination) { goTo(0); return; }
    setSubmitting(true);
    setError(null);
    try {
      const visibleAnswers = Object.fromEntries(questions.filter((q) => answers[q.key]).map((q) => [q.key, answers[q.key]]));
      const result = await publicContractService.create({
        ...details,
        country_id: destination.country.id,
        answers: visibleAnswers,
        full_name: person.full_name.trim().replace(/\s+/g, ' '),
        email: person.email.trim().toLowerCase(),
      });
      setContract(result.contract);
      setToken(result.access_token);
      setDownloaded(false);
      writeStore(sessionStorage, RESULT_KEY, { token: result.access_token, contract: result.contract, downloaded: false });
      writeStore(localStorage, DRAFT_KEY, null);
      goTo(3);
    } catch (submitError) {
      setError(errorMessage(submitError, 'تعذر إنشاء العقد. راجع البيانات وحاول مرة أخرى.'));
    } finally {
      setSubmitting(false);
    }
  };

  const download = async () => {
    if (!contract || !documentRef.current) return;
    setDownloading(true);
    setError(null);
    try {
      const pdf = await renderContractPdf(documentRef.current);
      pdf.save(contractFileName(contract));
      setDownloaded(true);
      if (token) {
        writeStore(sessionStorage, RESULT_KEY, { token, contract, downloaded: true });
        // Recording the download must never stop the visitor getting the file.
        publicContractService.recordDownload(contract.contract_number, token).catch(() => undefined);
      }
    } catch {
      setError('تعذر إنشاء ملف PDF. حدّث الصفحة وحاول مرة أخرى.');
    } finally {
      setDownloading(false);
    }
  };

  const startOver = () => {
    writeStore(sessionStorage, RESULT_KEY, null);
    writeStore(localStorage, DRAFT_KEY, null);
    setContract(null); setToken(null); setDownloaded(false);
    setCountryId(null); setAnswers({}); setPerson(EMPTY_PERSON); setQuote(null);
    goTo(0);
  };

  // The contract shows the details it was generated with: from the stored
  // result after a refresh, otherwise from the form.
  const documentDetails = (contract?.details as ContractDetails | null | undefined) ?? details;

  return (
    <div className="public-contract" dir="rtl" lang="ar">
      <header className="public-contract-header">
        <Link to="/" className="public-contract-brand"><img src={logo} alt="EduNasser" /></Link>
        <div>
          <h1>عقد الخدمة أونلاين</h1>
          <p>اختار وجهتك والباقة، اكتب بياناتك، ونزّل عقدك المختوم في دقائق.</p>
        </div>
      </header>

      <main className="public-contract-main" ref={topRef}>
        <ol className="public-contract-steps" aria-label="خطوات العقد">
          {STEPS.map((item, index) => {
            const Icon = item.icon;
            const state = index < step ? 'done' : index === step ? 'current' : 'todo';
            return (
              <li key={item.title} className={`public-contract-step ${state}`} aria-current={state === 'current' ? 'step' : undefined}>
                <span className="public-contract-step-icon">{state === 'done' ? <Check size={16} /> : <Icon size={16} />}</span>
                <span className="public-contract-step-title">{item.title}</span>
              </li>
            );
          })}
        </ol>

        {error && (
          <div className="portal-alert portal-alert-error" role="alert">
            <div><strong>تعذر إكمال الخطوة</strong><p>{error}</p></div>
          </div>
        )}

        {step === 0 && (
          <section className="portal-card portal-card-body">
            <h2 className="public-contract-heading">اختار الدولة اللي عايز تدرس فيها</h2>
            {loadingDestinations ? (
              <div className="portal-empty"><LoaderCircle className="portal-spin" /></div>
            ) : destinations.length === 0 ? (
              <div className="portal-empty"><h3>لا توجد وجهات متاحة حاليًا</h3><p>حاول لاحقًا أو تواصل معنا.</p></div>
            ) : (
              <div className="public-contract-countries">
                {destinations.map(({ country }) => (
                  <button key={country.id} type="button" className={`public-contract-country ${country.id === countryId ? 'selected' : ''}`} onClick={() => chooseCountry(country.id)}>
                    <CountryFlag code={country.code} fallback={country.country_flag} label={country.name} />
                    <span className="public-contract-country-text">
                      <strong>{countryNameAr(country.code, country.name)}</strong>
                      <small>{country.description || 'التقديم والقبول والتأشيرة'}</small>
                    </span>
                    {country.id === countryId ? <Check size={18} /> : <ArrowLeft size={18} />}
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {step === 1 && destination && (
          <section className="portal-card portal-card-body">
            <h2 className="public-contract-heading">باقة {countryNameAr(destination.country.code, destination.country.name)}</h2>
            <p className="public-contract-lead">أجب عن الأسئلة دي علشان نحسب سعرك وجدول الدفعات بالظبط.</p>
            <div className="public-contract-questions">
              {questions.map((question) => (
                <fieldset key={question.key} className="public-contract-question">
                  <legend>{question.label_ar || question.label}{question.required ? ' *' : ''}</legend>
                  <div className="public-contract-options">
                    {question.options.map((option) => (
                      <label key={option.value} className={`public-contract-option ${answers[question.key] === option.value ? 'selected' : ''}`}>
                        <input type="radio" name={question.key} value={option.value} checked={answers[question.key] === option.value} onChange={() => setAnswer(question.key, option.value)} />
                        <span>{option.label_ar || option.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>

            <div className="public-contract-price" aria-live="polite">
              {!answersComplete ? (
                <p>أجب عن الأسئلة المطلوبة علشان يظهر السعر.</p>
              ) : quoting && !quote ? (
                <p><LoaderCircle className="portal-spin" size={16} /> جاري حساب السعر...</p>
              ) : quote ? (
                <>
                  <div className="public-contract-price-total">
                    <small>{quote.payment_mode === 'full_advance' ? 'الإجمالي بعد خصم السداد المقدم' : 'إجمالي الخدمة'}</small>
                    <strong>{money(quote.payable_total, quote.currency)}</strong>
                    {Number(quote.discount_amount) > 0 && <span>بدل {money(quote.regular_total, quote.currency)} · وفرت {money(quote.discount_amount, quote.currency)}</span>}
                  </div>
                  <ul className="public-contract-schedule">
                    {quote.payment_schedule.map((row, index) => (
                      <li key={`${row.order}-${index}`}><span>{row.title}</span><b>{money(row.amount, quote.currency)}</b></li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>

            <div className="public-contract-actions">
              <button type="button" className="portal-button portal-button-outline" onClick={() => goTo(0)}><ArrowRight size={17} />تغيير الوجهة</button>
              <button type="button" className="portal-button portal-button-primary" disabled={!quote || quoting} onClick={() => goTo(2)}>متابعة لبياناتك<ArrowLeft size={17} /></button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="portal-card portal-card-body">
            <h2 className="public-contract-heading">بيانات الطرف الثاني</h2>
            <p className="public-contract-lead">البيانات دي هتظهر في العقد. الحقول اللي عليها نجمة مطلوبة.</p>
            <div className="portal-form-grid">
              <Field label="الاسم بالكامل *" value={person.full_name} onChange={(v) => setField('full_name', v)} autoComplete="name" wide />
              <Field label="البريد الإلكتروني *" type="email" value={person.email} onChange={(v) => setField('email', v)} autoComplete="email" dir="ltr" />
              <Field label="رقم الهاتف *" type="tel" value={person.phone} onChange={(v) => setField('phone', v)} autoComplete="tel" dir="ltr" />
              <Field label="الجنسية *" value={person.nationality} onChange={(v) => setField('nationality', v)} list={NATIONALITIES} />
              <Field label="الرقم القومي / رقم الهوية *" value={person.national_id} onChange={(v) => setField('national_id', v)} dir="ltr" />
              <Field label="رقم جواز السفر (إن وجد)" value={person.passport_number} onChange={(v) => setField('passport_number', v)} dir="ltr" />
              <Field label="العنوان *" value={person.address} onChange={(v) => setField('address', v)} autoComplete="street-address" wide />
              <Select label="المؤهل الدراسي *" value={person.qualification} onChange={(v) => setField('qualification', v)} options={QUALIFICATIONS} />
              <Select label="المرحلة المطلوبة *" value={person.study_level} onChange={(v) => setField('study_level', v)} options={STUDY_LEVELS} />
              <Field label="اسم ولي الأمر (اختياري)" value={person.guardian_name} onChange={(v) => setField('guardian_name', v)} />
              <Field label="رقم هوية ولي الأمر" value={person.guardian_national_id} onChange={(v) => setField('guardian_national_id', v)} dir="ltr" />
              <Field label="هاتف ولي الأمر" type="tel" value={person.guardian_phone} onChange={(v) => setField('guardian_phone', v)} dir="ltr" />
              <div className="portal-field portal-field-wide">
                <label htmlFor="pc-notes">ملاحظات (اختياري)</label>
                <textarea id="pc-notes" className="portal-textarea" dir="auto" value={person.file_notes} onChange={(event) => setField('file_notes', event.target.value)} placeholder="أي معلومة مهمة عايز فريقنا يعرفها" />
              </div>
            </div>
            <p className="public-contract-privacy"><ShieldCheck size={16} />بياناتك بتتحفظ عندنا بأمان علشان فريقنا يقدر يتابع معاك.</p>
            <div className="public-contract-actions">
              <button type="button" className="portal-button portal-button-outline" onClick={() => goTo(1)}><ArrowRight size={17} />رجوع للباقة</button>
              <button type="button" className="portal-button portal-button-primary" disabled={submitting} onClick={() => void submit()}>
                {submitting ? <LoaderCircle className="portal-spin" size={17} /> : <FileCheck2 size={17} />}
                {submitting ? 'جاري تجهيز العقد...' : 'إنشاء العقد'}
              </button>
            </div>
          </section>
        )}

        {step === 3 && contract && (
          <section className="public-contract-review">
            <div className="portal-contract-note">
              <CheckCircle2 size={18} />
              <span>{downloaded ? `تم تحميل عقدك رقم ${contract.contract_number}. احتفظ بالنسخة دي.` : `عقدك جاهز برقم ${contract.contract_number}. راجعه ثم نزّل نسخة PDF مختومة.`}</span>
            </div>
            <div className="public-contract-actions public-contract-actions-top">
              <button type="button" className="portal-button portal-button-primary" disabled={downloading} onClick={() => void download()}>
                {downloading ? <LoaderCircle className="portal-spin" size={17} /> : <Download size={17} />}
                {downloading ? 'جاري تجهيز ملف PDF...' : downloaded ? 'تحميل مرة أخرى' : 'تحميل العقد PDF'}
              </button>
              <button type="button" className="portal-button portal-button-outline" onClick={startOver}><RotateCcw size={17} />عقد جديد</button>
            </div>
            <div className="portal-contract-viewport">
              <ContractDocument contract={contract} details={documentDetails} sections={sections} documentRef={documentRef} />
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', autoComplete, dir = 'auto', wide = false, list }: {
  label: string; value: string; onChange: (value: string) => void; type?: string; autoComplete?: string; dir?: string; wide?: boolean; list?: string[];
}) {
  const id = `pc-${label.replace(/[^\p{L}\p{N}]+/gu, '-')}`;
  return (
    <div className={`portal-field ${wide ? 'portal-field-wide' : ''}`}>
      <label htmlFor={id}>{label}</label>
      <input id={id} className="portal-input" type={type} dir={dir} value={value} autoComplete={autoComplete} list={list ? `${id}-list` : undefined} onChange={(event) => onChange(event.target.value)} />
      {list && <datalist id={`${id}-list`}>{list.map((item) => <option key={item} value={item} />)}</datalist>}
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: string[] }) {
  const id = `pc-${label.replace(/[^\p{L}\p{N}]+/gu, '-')}`;
  return (
    <div className="portal-field">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="portal-select" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">اختر من القائمة</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </div>
  );
}
